import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { exchangeViewerToken } from '../lib/auth';

/**
 * Handles two redirect flows:
 *   1. Magic link from Supabase: contains access_token/refresh_token in URL hash.
 *      Supabase's client auto-detects these (detectSessionInUrl: true).
 *   2. Viewer link: has ?viewer_token=... in query string. We exchange it
 *      for a Supabase session via the verify-viewer-link function.
 */
export function AuthCallback() {
  const [status, setStatus] = useState<'working' | 'error'>('working');
  const [msg, setMsg] = useState('Signing you in…');

  useEffect(() => {
    const url = new URL(window.location.href);
    const viewerToken = url.searchParams.get('viewer_token');

    if (viewerToken) {
      (async () => {
        try {
          const { access_token } = await exchangeViewerToken(viewerToken);
          // Install the viewer session. Refresh token is absent — viewer sessions
          // don't refresh; they expire when the token expires.
          const { error } = await supabase.auth.setSession({
            access_token,
            refresh_token: '',
          });
          if (error) throw error;
          window.location.replace('/');
        } catch (err) {
          setStatus('error');
          setMsg(err instanceof Error ? err.message : 'Link invalid or expired.');
        }
      })();
      return;
    }

    // Magic-link flow: supabase-js parses the URL hash automatically.
    // Wait for the session to be established, then redirect.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN') {
        window.location.replace('/');
      }
    });

    // Fallback timeout if nothing happens
    const timeout = setTimeout(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        window.location.replace('/');
      } else {
        setStatus('error');
        setMsg('Sign-in link could not be processed. Request a new one.');
      }
    }, 5000);

    return () => {
      sub.subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  return (
    <div className="login-wrap">
      <div className="login-card">
        <h1>Eisenhower Matrix</h1>
        <p className={status === 'error' ? 'err' : ''}>{msg}</p>
        {status === 'error' && <a href="/">Back to sign in</a>}
      </div>
    </div>
  );
}
